import React, { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tabs } from "antd"
import { ColumnsType } from "antd/lib/table"
import { notifications } from "notifications"
import { useStore } from "stores/utils/use-store"
import {
  CreateDailyMissionRequest,
  DAILY_MISSION_KIND_OPTIONS,
  DailyMission,
  DailyMissionKind,
  GamePlatformType,
  NativeGame,
  UpdateDailyMissionRequest,
} from "../store/types"
import { DailyMissionDraft, validateDailyMission } from "../helper/daily-missions-validation"

type Props = {
  game: NativeGame | null
  onClose: () => void
}

type KindConfig = {
  requiredCountLabel: string
  requiredCountPlaceholder: string
  taskTokenHelp: string
  taskTokenPlaceholder: string
  titlePlaceholder: string
  descriptionPlaceholder: string
  defaultWindowDays: number | null
}

const KIND_CONFIG: Record<DailyMissionKind, KindConfig> = {
  WATCH_AD: {
    requiredCountLabel: "Ads to watch",
    requiredCountPlaceholder: "e.g. 3",
    taskTokenHelp: "Watch-ad postback token. Credited when the matching watch-ad event fires.",
    taskTokenPlaceholder: "e.g. ad_shown",
    titlePlaceholder: "Watch 3 ads today",
    descriptionPlaceholder: "Watch <requiredCount> ads to earn $<rewardUsd>",
    defaultWindowDays: null,
  },
  DAILY_TASK: {
    requiredCountLabel: "Tasks to complete",
    requiredCountPlaceholder: "e.g. 1",
    taskTokenHelp: "Daily-task postback token (per task or per day index). Credited when the matching daily-task event fires.",
    taskTokenPlaceholder: "e.g. completed_daily_quest",
    titlePlaceholder: "Complete 1 daily quest",
    descriptionPlaceholder: "Finish <requiredCount> daily quest to earn $<rewardUsd>",
    defaultWindowDays: 1,
  },
  LOGIN_STREAK: {
    requiredCountLabel: "Consecutive login days",
    requiredCountPlaceholder: "e.g. 7",
    taskTokenHelp: "Session/login-streak postback token. Credited once per day when the account opens the app.",
    taskTokenPlaceholder: "e.g. login_streak",
    titlePlaceholder: "Login 7 days in a row",
    descriptionPlaceholder: "Login <requiredCount> days in a row to earn $<rewardUsd>",
    defaultWindowDays: null,
  },
}

const PLATFORM_LABELS: Record<GamePlatformType, string> = {
  ANDROID: "Android",
  IOS: "iOS",
}

const WINDOW_DAYS_HELP
  = "Empty = one-shot lifetime accumulator (claim once, ever). Set to N = user can claim once every N days."

const emptyDraft = (gameId: string, platform: GamePlatformType): DailyMissionDraft => ({
  gameId,
  platform,
  kind: DailyMissionKind.WATCH_AD,
  taskToken: "",
  title: "",
  descriptionTmpl: "",
  rewardUsd: 0,
  requiredCount: 1,
  windowDays: KIND_CONFIG[DailyMissionKind.WATCH_AD].defaultWindowDays,
  enabled: true,
})

export const DailyMissionsModal: FC<Props> = observer(({ game, onClose }) => {
  const { dataStore: { nativeGamesControlStore } } = useStore()
  // Independent per-platform list — switching tabs reloads the rows so each
  // platform's daily missions stay isolated.
  const [rowsByPlatform, setRowsByPlatform] = useState<Record<GamePlatformType, DailyMission[]>>({
    ANDROID: [],
    IOS: [],
  })
  const [activePlatform, setActivePlatform] = useState<GamePlatformType>(GamePlatformType.ANDROID)
  const [loadingPlatform, setLoadingPlatform] = useState<GamePlatformType | null>(null)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState<DailyMission | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [draft, setDraft] = useState<DailyMissionDraft>(() => emptyDraft(game?.id ?? "", GamePlatformType.ANDROID))

  const load = useCallback(async(gameId: string, platform: GamePlatformType) => {
    setLoadingPlatform(platform)
    try {
      const data = await nativeGamesControlStore.getDailyMissions(gameId, platform)
      setRowsByPlatform(prev => ({ ...prev, [platform]: data }))
    } catch (err: any) {
      notifications.error(err?.message || "Failed to load daily missions")
    } finally {
      setLoadingPlatform(null)
    }
  }, [nativeGamesControlStore])

  useEffect(() => {
    if (game) {
      void load(game.id, activePlatform)
    }
  }, [game, activePlatform, load])

  if (!game) return null

  const openCreate = () => {
    setEditing(null)
    setDraft(emptyDraft(game.id, activePlatform))
    setIsCreateOpen(true)
  }

  const openEdit = (row: DailyMission) => {
    setEditing(row)
    setDraft({
      gameId: row.gameId,
      platform: row.platform,
      kind: row.kind,
      taskToken: row.taskToken,
      title: row.title,
      descriptionTmpl: row.descriptionTmpl,
      rewardUsd: row.rewardUsd,
      requiredCount: row.requiredCount,
      windowDays: row.windowDays,
      enabled: row.enabled,
    })
    setIsCreateOpen(true)
  }

  const closeEditor = () => {
    setIsCreateOpen(false)
    setEditing(null)
  }

  const onSave = async() => {
    const err = validateDailyMission(draft)
    if (err) {
      notifications.error(err.message)

      return
    }
    setSaving(true)
    try {
      if (editing) {
        // Platform is immutable post-create on the BE; we intentionally
        // omit it from the update payload.
        const patch: UpdateDailyMissionRequest = {
          kind: draft.kind,
          taskToken: draft.taskToken,
          title: draft.title,
          descriptionTmpl: draft.descriptionTmpl,
          rewardUsd: draft.rewardUsd,
          requiredCount: draft.requiredCount,
          windowDays: draft.windowDays ?? null,
          enabled: draft.enabled,
        }
        await nativeGamesControlStore.updateDailyMission(editing.id, patch)
        notifications.success("Daily mission updated")
      } else {
        const payload: CreateDailyMissionRequest = {
          gameId: game.id,
          platform: draft.platform ?? activePlatform,
          kind: draft.kind!,
          taskToken: draft.taskToken!,
          title: draft.title!,
          descriptionTmpl: draft.descriptionTmpl!,
          rewardUsd: draft.rewardUsd!,
          requiredCount: draft.requiredCount!,
          windowDays: draft.windowDays ?? null,
          enabled: draft.enabled ?? true,
        }
        await nativeGamesControlStore.createDailyMission(payload)
        notifications.success("Daily mission created")
      }
      closeEditor()
      await load(game.id, activePlatform)
    } catch (e: any) {
      notifications.error(e?.message || "Failed to save daily mission")
    } finally {
      setSaving(false)
    }
  }

  const onDelete = (row: DailyMission) => {
    Modal.confirm({
      title: "Delete daily mission",
      content: `Delete "${row.title}"? This cannot be undone.`,
      okType: "danger",
      okText: "Delete",
      cancelText: "Cancel",
      onOk: async() => {
        try {
          await nativeGamesControlStore.deleteDailyMission(row.id)
          notifications.success("Daily mission deleted")
          await load(game.id, activePlatform)
        } catch (e: any) {
          notifications.error(e?.message || "Failed to delete daily mission")
        }
      },
    })
  }

  const columns: ColumnsType<DailyMission> = [
    { title: "Kind", dataIndex: "kind", key: "kind", width: 140 },
    { title: "Task Token", dataIndex: "taskToken", key: "taskToken", width: 180 },
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Description Template", dataIndex: "descriptionTmpl", key: "descriptionTmpl" },
    { title: "Reward ($)", dataIndex: "rewardUsd", key: "rewardUsd", width: 100 },
    { title: "Required Count", dataIndex: "requiredCount", key: "requiredCount", width: 130 },
    {
      title: "Window (days)",
      dataIndex: "windowDays",
      key: "windowDays",
      width: 120,
      render: (v: number | null) => v ?? "—",
    },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      width: 100,
      render: (v: boolean) => (v ? "Yes" : "No"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_: any, row: DailyMission) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>Edit</Button>
          <Button size="small" danger onClick={() => onDelete(row)}>Delete</Button>
        </Space>
      ),
    },
  ]

  const tabItems = (Object.values(GamePlatformType) as GamePlatformType[]).map(platform => ({
    key: platform,
    label: PLATFORM_LABELS[platform],
    children: (
      <Table<DailyMission>
        dataSource={rowsByPlatform[platform]}
        columns={columns}
        rowKey="id"
        pagination={false}
        loading={loadingPlatform === platform}
        bordered
        size="small"
      />
    ),
  }))

  return (
    <>
      <Modal
        open
        onCancel={onClose}
        title={`Daily Missions — ${game.name}`}
        width={1000}
        footer={[
          <Button key="create" type="primary" onClick={openCreate}>
            {`New ${PLATFORM_LABELS[activePlatform]} Daily Mission`}
          </Button>,
          <Button key="close" onClick={onClose}>Close</Button>,
        ]}
      >
        <p style={{ marginBottom: 12 }}>
          A game may have 0..N daily missions per platform, one per kind (WATCH_AD / DAILY_TASK / LOGIN_STREAK)
          is typical. Validation enforces requiredCount ≥ 1 and windowDays ≥ 1 (or empty for no windowing).
        </p>
        <Tabs
          activeKey={activePlatform}
          onChange={key => setActivePlatform(key as GamePlatformType)}
          items={tabItems}
        />
      </Modal>

      <Modal
        open={isCreateOpen}
        onCancel={closeEditor}
        title={editing
          ? `Edit Daily Mission (${PLATFORM_LABELS[editing.platform]})`
          : `New ${PLATFORM_LABELS[activePlatform]} Daily Mission`}
        okText={editing ? "Save" : "Create"}
        confirmLoading={saving}
        onOk={onSave}
      >
        <Form layout="vertical">
          <Form.Item label="Kind" required>
            <Select
              value={draft.kind}
              options={DAILY_MISSION_KIND_OPTIONS}
              onChange={value => setDraft(d => {
                const nextKind = value as DailyMissionKind
                const cfg = KIND_CONFIG[nextKind]

                return {
                  ...d,
                  kind: nextKind,
                  windowDays: cfg.defaultWindowDays,
                }
              })}
            />
          </Form.Item>
          <Form.Item
            label="Task Token"
            required
            help={draft.kind ? KIND_CONFIG[draft.kind].taskTokenHelp : ""}
          >
            <Input
              value={draft.taskToken}
              onChange={e => setDraft(d => ({ ...d, taskToken: e.target.value }))}
              placeholder={draft.kind ? KIND_CONFIG[draft.kind].taskTokenPlaceholder : ""}
            />
          </Form.Item>
          <Form.Item label="Title" required>
            <Input
              value={draft.title}
              onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
              placeholder={draft.kind ? KIND_CONFIG[draft.kind].titlePlaceholder : ""}
            />
          </Form.Item>
          <Form.Item label="Description Template" required>
            <Input.TextArea
              rows={2}
              value={draft.descriptionTmpl}
              onChange={e => setDraft(d => ({ ...d, descriptionTmpl: e.target.value }))}
              placeholder={draft.kind ? KIND_CONFIG[draft.kind].descriptionPlaceholder : ""}
            />
          </Form.Item>
          <Form.Item label="Reward (USD)" required>
            <InputNumber
              min={0}
              step={0.01}
              value={draft.rewardUsd}
              style={{ width: "100%" }}
              onChange={value => setDraft(d => ({ ...d, rewardUsd: Number(value ?? 0) }))}
            />
          </Form.Item>
          <Form.Item
            label={draft.kind ? KIND_CONFIG[draft.kind].requiredCountLabel : "Required Count"}
            required
          >
            <InputNumber
              min={1}
              step={1}
              value={draft.requiredCount}
              placeholder={draft.kind ? KIND_CONFIG[draft.kind].requiredCountPlaceholder : ""}
              style={{ width: "100%" }}
              onChange={value => setDraft(d => ({ ...d, requiredCount: Number(value ?? 1) }))}
            />
          </Form.Item>
          <Form.Item label="Window (days)" help={WINDOW_DAYS_HELP}>
            <InputNumber
              min={1}
              step={1}
              value={draft.windowDays ?? undefined}
              style={{ width: "100%" }}
              onChange={value => setDraft(d => ({ ...d, windowDays: value == null ? null : Number(value) }))}
            />
          </Form.Item>
          <Form.Item label="Enabled">
            <Switch
              checked={draft.enabled ?? true}
              onChange={checked => setDraft(d => ({ ...d, enabled: checked }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
})
